import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/shared/contexts'
import type {
  AppPlatform,
  AppUpdateSettings,
  AppUpdateType,
  AppVersionUpdateRequest,
} from '@/shared/api'
import { Button, Card, Input } from '@/shared/ui'

// Тип обновления → подпись в интерфейсе
const UPDATE_TYPE_LABELS: Record<AppUpdateType, string> = {
  off: 'Выключено',
  recommended: 'Рекомендуемое обновление',
  forced: 'Обязательное обновление',
}

const UPDATE_TYPE_OPTIONS: AppUpdateType[] = ['off', 'recommended', 'forced']

// Допустимый формат версии: до 4 числовых сегментов — только ASCII-цифры,
// как в бэкенде (проверяем значения после trim, поэтому $ достаточно строг)
const VERSION_PATTERN = /^[0-9]{1,4}(?:\.[0-9]{1,4}){0,3}$/

// Хосты магазинов, зеркалят STORE_HOSTS бэкенда
const STORE_HOSTS: Record<AppPlatform, string[]> = {
  android: ['play.google.com'],
  ios: ['apps.apple.com', 'itunes.apple.com'],
}

const STORE_URL_ERRORS: Record<AppPlatform, string> = {
  android: 'Ссылка должна вести в Google Play',
  ios: 'Ссылка должна вести в App Store',
}

// https-ссылка с хостом магазина платформы; хвостовой слэш не обязателен
const isValidStoreUrl = (platform: AppPlatform, value: string): boolean => {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return false
  }
  if (parsed.protocol !== 'https:') return false
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
  return STORE_HOSTS[platform].includes(host)
}

// Черновик формы: всё, что сохраняется через PUT (is_active меняется тумблером)
type Draft = Omit<AppVersionUpdateRequest, 'is_active'>

const toDraft = (settings: AppUpdateSettings): Draft => ({
  update_type: settings.update_type,
  min_version: settings.min_version,
  latest_version: settings.latest_version,
  store_url: settings.store_url,
  forced_title: settings.forced_title,
  forced_message: settings.forced_message,
  recommended_title: settings.recommended_title,
  recommended_message: settings.recommended_message,
})

const DRAFT_FIELDS: Array<keyof Draft> = [
  'update_type',
  'min_version',
  'latest_version',
  'store_url',
  'forced_title',
  'forced_message',
  'recommended_title',
  'recommended_message',
]

const isDraftEqual = (left: Draft, right: Draft): boolean =>
  DRAFT_FIELDS.every((field) => left[field] === right[field])

const parseVersion = (value: string): number[] =>
  value.split('.').map((part) => Number(part))

const compareVersions = (left: string, right: string): number => {
  const leftParts = parseVersion(left)
  const rightParts = parseVersion(right)
  const length = Math.max(leftParts.length, rightParts.length)
  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? 0
    const rightPart = rightParts[index] ?? 0
    if (leftPart !== rightPart) return leftPart < rightPart ? -1 : 1
  }
  return 0
}

const isZeroVersion = (value: string): boolean => compareVersions(value, '0.0.0') === 0

const validateDraft = (
  platform: AppPlatform,
  draft: Draft,
  isActive: boolean
): string | null => {
  const minVersion = draft.min_version.trim()
  const latestVersion = draft.latest_version.trim()

  if (!VERSION_PATTERN.test(minVersion)) {
    return 'Укажите корректную минимальную версию, например 1.2.3'
  }
  if (!VERSION_PATTERN.test(latestVersion)) {
    return 'Укажите корректную актуальную версию, например 1.2.3'
  }
  if (compareVersions(minVersion, latestVersion) > 0) {
    return 'Минимальная версия не может быть выше актуальной'
  }
  if (!isActive || draft.update_type === 'off') {
    return null
  }

  if (isZeroVersion(latestVersion)) {
    return 'Укажите актуальную версию для включения обновления'
  }

  const storeUrl = draft.store_url.trim()
  if (!isValidStoreUrl(platform, storeUrl)) {
    return STORE_URL_ERRORS[platform]
  }

  if (draft.update_type === 'forced' && isZeroVersion(minVersion)) {
    return 'Укажите минимальную версию для обязательного обновления'
  }

  return null
}

const toRequest = (draft: Draft, isActive: boolean): AppVersionUpdateRequest => ({
  is_active: isActive,
  update_type: draft.update_type,
  min_version: draft.min_version.trim(),
  latest_version: draft.latest_version.trim(),
  store_url: draft.store_url.trim(),
  forced_title: draft.forced_title,
  forced_message: draft.forced_message,
  recommended_title: draft.recommended_title,
  recommended_message: draft.recommended_message,
})

interface AppVersionFormProps {
  platform: AppPlatform
  settings: AppUpdateSettings
  onSave: (request: AppVersionUpdateRequest) => void
  onToggle: (isActive: boolean) => void
  onDirtyChange: (dirty: boolean) => void
  saving: boolean
  toggling: boolean
}

export const AppVersionForm: React.FC<AppVersionFormProps> = ({
  platform,
  settings,
  onSave,
  onToggle,
  onDirtyChange,
  saving,
  toggling,
}) => {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('appVersions', 'edit')

  const [draft, setDraft] = useState<Draft>(() => toDraft(settings))
  const [error, setError] = useState<string | null>(null)

  // Синхронизируем черновик при получении новых настроек с сервера.
  // Сравниваем по ссылке: React Query сохраняет идентичность, если данные не изменились.
  const syncedSettingsRef = useRef<AppUpdateSettings | null>(null)
  useEffect(() => {
    if (syncedSettingsRef.current === settings) return
    syncedSettingsRef.current = settings
    setDraft(toDraft(settings))
    setError(null)
  }, [settings])

  // Есть ли несохранённые правки относительно текущих настроек с сервера
  const isDirty = !isDraftEqual(draft, toDraft(settings))
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const patchDraft = (patch: Partial<Draft>) => {
    setError(null)
    setDraft((prev) => ({ ...prev, ...patch }))
  }

  const handleSave = () => {
    const validationError = validateDraft(platform, draft, settings.is_active)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    onSave(toRequest(draft, settings.is_active))
  }

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEdit || saving || toggling) return
    setError(null)
    onToggle(event.target.checked)
  }

  const fieldsDisabled = !canEdit || saving || toggling

  return (
    <Card>
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Настройки обновления</h2>
            <p className="mt-1 text-sm text-gray-500">
              Параметры кампании обновления приложения «Звено»
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-gray-700 focus:ring-gray-500"
              checked={settings.is_active}
              disabled={!canEdit || saving || toggling}
              onChange={handleToggle}
            />
            Кампания активна
          </label>
        </div>

        <p className="text-xs text-gray-500">
          Тумблер кампании применяется сразу к сохранённым настройкам, остальные поля — по
          кнопке «Сохранить».
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700" htmlFor="app-version-type">
              Тип обновления
            </label>
            <select
              id="app-version-type"
              className="admin-input"
              value={draft.update_type}
              disabled={fieldsDisabled}
              onChange={(e) => patchDraft({ update_type: e.target.value as AppUpdateType })}
            >
              {UPDATE_TYPE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {UPDATE_TYPE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Минимальная версия"
            placeholder="Например, 1.2.0"
            value={draft.min_version}
            disabled={fieldsDisabled}
            maxLength={32}
            onChange={(e) => patchDraft({ min_version: e.target.value })}
          />

          <Input
            label="Актуальная версия"
            placeholder="Например, 1.4.0"
            value={draft.latest_version}
            disabled={fieldsDisabled}
            maxLength={32}
            onChange={(e) => patchDraft({ latest_version: e.target.value })}
          />

          <div className="sm:col-span-2">
            <Input
              label="Ссылка на магазин приложений"
              placeholder={
                platform === 'android'
                  ? 'https://play.google.com/store/apps/details?id=...'
                  : 'https://apps.apple.com/app/id...'
              }
              value={draft.store_url}
              disabled={fieldsDisabled}
              maxLength={512}
              onChange={(e) => patchDraft({ store_url: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900">Обязательное обновление</h3>

          <Input
            label="Заголовок"
            value={draft.forced_title}
            disabled={fieldsDisabled}
            maxLength={255}
            onChange={(e) => patchDraft({ forced_title: e.target.value })}
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700" htmlFor="forced-message">
              Текст
            </label>
            <textarea
              id="forced-message"
              className="admin-input"
              rows={3}
              value={draft.forced_message}
              disabled={fieldsDisabled}
              onChange={(e) => patchDraft({ forced_message: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900">Рекомендуемое обновление</h3>

          <Input
            label="Заголовок"
            value={draft.recommended_title}
            disabled={fieldsDisabled}
            maxLength={255}
            onChange={(e) => patchDraft({ recommended_title: e.target.value })}
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700" htmlFor="recommended-message">
              Текст
            </label>
            <textarea
              id="recommended-message"
              className="admin-input"
              rows={3}
              value={draft.recommended_message}
              disabled={fieldsDisabled}
              onChange={(e) => patchDraft({ recommended_message: e.target.value })}
            />
          </div>
        </div>

        {error && <p className="text-sm text-error-600">{error}</p>}

        {!canEdit && (
          <p className="text-sm text-gray-500">
            У вашей роли нет прав на изменение настроек — доступен только просмотр.
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <span className="text-xs text-gray-500">
            {settings.updated_at
              ? `Последнее изменение: ${new Date(settings.updated_at * 1000).toLocaleString('ru-RU')} · ${settings.updated_by_name || 'Система'}`
              : 'Настройки ещё не изменялись'}
          </span>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canEdit || saving || toggling || !isDirty}
            isLoading={saving}
          >
            Сохранить
          </Button>
        </div>
      </div>
    </Card>
  )
}
